// Landing page: three role entry points (PROTOCOL.md §3).
//
// Observer / Facilitator and Participant require a way to reach a
// backend. Two paths qualify: a running controller addressed by
// `?lab_id=<lab>` (HTTP for /api/auth/role, WS for the live
// protocol) or the in-browser SharedWorker broker enabled with
// `?ipc=1`. Without one of those query params there is nothing for
// those roles to talk to, so the tiles stay disabled even when the
// controller probe succeeds. Designer is offline-capable — it boots
// against localStorage and stays enabled regardless.
//
// IPC mode (`?ipc=1`) routes Observer + Participant through an
// in-browser SharedWorker broker — useful for demos and for
// paired-tab dry-runs. The experimental box below lets users opt
// into it from the UI.

import { Activity, Eye, FlaskConical } from 'lucide-react';
import { HelpHint, Logo } from '@usability-suite/ui';
import { ipcModeEnabled } from '@usability-suite/protocol-client';
import { useControllerHealth } from './useControllerHealth';

// Hosts that serve a static demo build (e.g., the Cloudflare-deployed
// marketing copy at helm-ux.com). No controller runs there, so the
// reachable/unreachable banner is just noise — suppress it.
const DEMO_HOST_SUFFIX = 'helm-ux.com';

const CONTACT_EMAIL = 'ready@helm-ux.com';

function isDemoDeployment(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === DEMO_HOST_SUFFIX || host.endsWith(`.${DEMO_HOST_SUFFIX}`);
}

function hasLabIdParam(): boolean {
  if (typeof window === 'undefined') return false;
  const value = new URLSearchParams(window.location.search).get('lab_id');
  return value !== null && value.length > 0;
}

interface RoleEntry {
  href: string;
  title: string;
  description: string;
  icon: typeof Eye;
  /** True if this role can be entered without a running controller. */
  offline: boolean;
}

const ROLES: RoleEntry[] = [
  {
    href: '#/observer',
    title: 'Observer / Facilitator',
    description:
      'Drive a session or watch one. Same dashboard; capabilities gated by role.',
    icon: Eye,
    offline: false,
  },
  {
    href: '#/participant',
    title: 'Participant',
    description: 'Claim a seat, follow instructions, complete tasks.',
    icon: Activity,
    offline: false,
  },
  {
    href: '#/design',
    title: 'Study Designer',
    description: 'Build studies, surveys, scenarios. Online or offline.',
    icon: FlaskConical,
    offline: true,
  },
];

export function Landing() {
  const demo = isDemoDeployment();
  const ipc = ipcModeEnabled();
  const hasLabId = hasLabIdParam();
  // Skip the /api/health probe entirely when there's no ?lab_id= —
  // we couldn't authenticate against the Worker / Durable Object
  // anyway, so there's no reason to wake it up. IPC mode replaces
  // the controller with an in-browser broker, so it also doesn't
  // need the probe.
  const health = useControllerHealth({ enabled: hasLabId && !ipc });
  const probing = health.state === 'unknown' && hasLabId && !ipc;
  // Observer + Participant need either a controller addressed by
  // ?lab_id= or the in-browser broker (?ipc=1). Without one of those
  // there's nothing to authenticate against, so the tiles stay
  // disabled regardless of the health probe.
  const hasCredentials = hasLabId || ipc;
  // In IPC mode the broker stands in for the controller — treat the
  // backend as reachable so the tiles light up without /api/health.
  const controllerReady = ipc || health.state === 'ready';
  const ready = hasCredentials && controllerReady;
  const disabledReason = demo
    ? 'demo-deployment'
    : !hasCredentials
      ? 'missing-credentials'
      : probing
        ? 'probing'
        : 'controller-unreachable';
  const disabledTooltip = demo
    ? 'Demo deployment — no controller is running.'
    : !hasCredentials
      ? 'Open this page with ?lab_id=<your-lab> or enable the experimental IPC mode below.'
      : probing
        ? 'Checking controller…'
        : 'Controller not reachable — start controllerd to enable this role.';

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col items-stretch justify-center px-6 py-12">
      <div className="flex items-center gap-4">
        <Logo className="h-16" />
        <h1 className="text-3xl font-bold tracking-tight">Helm</h1>
        <HelpHint label="About Helm">
          Helm is a software suite for usability evaluations in lab settings: design the
          questionnaire and task flow, facilitate a live session, observe and code in real
          time, then export the results. Pick the role you&apos;re filling today.
        </HelpHint>
      </div>
      <p className="mt-4 text-muted-foreground">
        A software suite for designing, running, and analyzing usability evaluations,
        tailored for lab environments. Pick a role.
      </p>
      {demo || !hasLabId || ipc ? null : <ControllerStatus health={health} />}
      {ipc ? <IpcBanner /> : <IpcOffer />}
      <nav className="mt-8 grid gap-4 sm:grid-cols-3" aria-label="Roles">
        {ROLES.map((r) => {
          const enabled = r.offline || ready;
          return enabled ? (
            <a
              key={r.href}
              href={r.href}
              data-role={r.href.replace('#/', '')}
              className="group flex flex-col rounded-md border border-border bg-card p-5 text-card-foreground transition hover:border-primary"
            >
              <r.icon className="size-6 text-primary" />
              <span className="mt-3 text-base font-semibold">{r.title}</span>
              <span className="mt-1 text-sm text-muted-foreground">{r.description}</span>
            </a>
          ) : (
            <button
              key={r.href}
              type="button"
              disabled
              data-role={r.href.replace('#/', '')}
              data-disabled-reason={disabledReason}
              title={disabledTooltip}
              aria-label={`${r.title} — ${disabledTooltip}`}
              className="group flex cursor-not-allowed flex-col rounded-md border border-border bg-card/40 p-5 text-card-foreground/60 opacity-60"
            >
              <r.icon className="size-6 text-muted-foreground" />
              <span className="mt-3 text-base font-semibold">{r.title}</span>
              <span className="mt-1 text-sm text-muted-foreground">{r.description}</span>
            </button>
          );
        })}
      </nav>
      <p className="mt-10 text-xs text-muted-foreground">
        Interested in Helm?{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-foreground">
          {CONTACT_EMAIL}
        </a>
      </p>
    </main>
  );
}

function setIpcParam(on: boolean): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (on) url.searchParams.set('ipc', '1');
  else url.searchParams.delete('ipc');
  // Reload so every app (and the SharedWorker) boots with the new mode.
  window.location.href = url.toString();
}

function IpcBanner() {
  return (
    <div
      role="status"
      data-testid="ipc-banner"
      className="mt-4 flex items-start justify-between gap-3 rounded-md border border-sky-400/40 bg-sky-50/40 p-3 text-sm text-sky-900 dark:bg-sky-950/30 dark:text-sky-200"
    >
      <div>
        <p className="font-medium">
          <span className="mr-1 rounded bg-sky-200/60 px-1 py-0.5 text-[10px] uppercase tracking-wide text-sky-900 dark:bg-sky-800/60 dark:text-sky-100">
            Experimental
          </span>
          Limited evaluation from the browser.
        </p>
        <p className="mt-1 text-xs opacity-80">
          Open Observer in one tab and Participant in another to pair them. No controller
          required.
        </p>
      </div>
      <button
        type="button"
        data-testid="ipc-banner-disable"
        onClick={() => setIpcParam(false)}
        className="rounded border border-sky-400/40 px-2 py-1 text-xs hover:bg-sky-100/40 dark:hover:bg-sky-900/40"
      >
        Disable
      </button>
    </div>
  );
}

function IpcOffer() {
  return (
    <div
      data-testid="ipc-offer"
      className="mt-3 flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 p-3 text-xs text-muted-foreground"
    >
      <p>
        <span className="mr-1 rounded bg-amber-200/40 px-1 py-0.5 text-[10px] uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
          Experimental
        </span>
        Run limited evaluation from the browser.
      </p>
      <button
        type="button"
        data-testid="ipc-offer-enable"
        onClick={() => setIpcParam(true)}
        className="rounded border border-border px-2 py-1 text-xs hover:border-primary hover:text-foreground"
      >
        Enable
      </button>
    </div>
  );
}

function ControllerStatus({
  health,
}: {
  health: ReturnType<typeof useControllerHealth>;
}) {
  if (health.state === 'unknown') return null;
  if (health.state === 'ready') {
    return (
      <p className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground">
        <span className="inline-block size-1.5 rounded-full bg-emerald-500" />
        Controller reachable.
      </p>
    );
  }
  return (
    <div
      role="status"
      className="mt-4 rounded-md border border-amber-400/40 bg-amber-50/40 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
      data-testid="controller-unreachable"
    >
      <p className="font-medium">Controller not reachable.</p>
      <p className="mt-1 text-xs opacity-80">
        Start <code>controllerd</code> to enable Observer/Facilitator and Participant
        roles. The Study Designer works offline.
      </p>
      <p className="mt-1 text-[10px] uppercase tracking-wide opacity-60">
        {health.reason}
      </p>
    </div>
  );
}
